import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Compass } from 'lucide-react';

export interface ServiceInfoData {
  acknowledged: boolean;
  sources?: string[];
  note?: string;
}

interface ServiceInfoPromptProps {
  onComplete: (data: ServiceInfoData) => void;
  onCancel: () => void;
}

const SOURCE_OPTIONS = ['OEM', 'Alldata', 'Mitchell', 'Identifix', 'TSB', 'Other'] as const;

export function ServiceInfoPrompt({ onComplete, onCancel }: ServiceInfoPromptProps) {
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [showExpanded, setShowExpanded] = useState(false);

  const handleYes = () => {
    setShowExpanded(true);
  };

  const handleNotYet = () => {
    onComplete({ acknowledged: false });
  };

  const handleStartWork = () => {
    onComplete({
      acknowledged: true,
      sources: selectedSources.length > 0 ? selectedSources : undefined,
      note: note.trim() || undefined,
    });
  };

  const toggleSource = (source: string) => {
    setSelectedSources(prev =>
      prev.includes(source)
        ? prev.filter(s => s !== source)
        : [...prev, source]
    );
  };

  return (
    <div
      data-testid="service-info-prompt"
      className="rounded-lg border bg-card p-3 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Compass className="h-4 w-4 text-teal-600 shrink-0" />
        <span>Before starting — was service information consulted for this work?</span>
      </div>

      {!showExpanded ? (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleYes}
            className="text-xs"
            data-testid="service-info-yes"
          >
            Yes
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleNotYet}
            className="text-xs"
            data-testid="service-info-not-yet"
          >
            Not yet
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancel}
            className="text-xs ml-auto text-muted-foreground"
            data-testid="service-info-cancel"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">Source (optional):</p>
            <div className="flex flex-wrap gap-1.5">
              {SOURCE_OPTIONS.map(source => (
                <Badge
                  key={source}
                  variant={selectedSources.includes(source) ? 'default' : 'outline'}
                  className="cursor-pointer text-xs select-none transition-colors"
                  onClick={() => toggleSource(source)}
                  data-testid={`service-info-source-${source}`}
                >
                  {source}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <Textarea
              placeholder="Procedure, spec, bulletin, or quick note (optional)"
              value={note}
              onChange={e => setNote(e.target.value)}
              className="min-h-[48px] text-xs resize-none"
              maxLength={200}
            />
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleStartWork}
              className="text-xs gap-1.5"
              data-testid="service-info-start-work"
            >
              ▶ Start Work
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onCancel}
              className="text-xs text-muted-foreground"
              data-testid="service-info-cancel-expanded"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
